import {
  ClaimIcon,
  DocumentPlaceholderIcon,
  ImpactClaimPlaceholderIcon,
  PartnerPlaceholderIcon,
  ProductPlaceholderIcon,
} from 'assets/img/menu';
import DrawerComponentItemIcon from 'Components/Drawer/DrawerComponentItemIcon';
import DrawerComponentItemTitle from 'Components/Drawer/DrawerComponentItemTitle';
import ProductTitle from 'Components/Drawer/ProductTitle';
import Icon, { TIconName } from 'DesignSystem/Primitives/Icon/Icon';
import { ECustomerPlan, TCustomerPlanType } from 'FeatureModules/Auth/types/company.types';
import DocumentTitle from 'FeatureModules/Document/Components/DocumentTitle';
import QuestionnaireTitle from 'FeatureModules/Document/Components/QuestionnaireTitle';
import ClaimTitle from 'FeatureModules/ImpactClaim/Components/ClaimTitle';
import PartnerName from 'FeatureModules/Partner/Components/PartnerName/PartnerName';
import React, { CSSProperties, FC } from 'react';
import theme from 'styles/theme';
import ActivityTitle from './ActivityTitle';
import PlotTitle from './PlotTitle';
import SiteTitle from './SiteTitle';

export type TDrawerSubMenuItem = {
  label: string;
  to: (params: { [key: string]: string }) => string;
  activeRoutes?: string[];
  dataTestId: string;
  visible?: boolean;
} & (
  | {
      /**
       * Use the iconName instead
       * @deprecated
       */
      Icon: FC;
      /**
       * Remove this prop when we have a better solution for defining svg colors
       * @deprecated
       */
      activeClassStyle?: CSSProperties | { [key: string]: CSSProperties };
    }
  | { iconName: TIconName }
);

export type TDrawerMenuItem = {
  label: string;
  to: string;
  submenu?: TDrawerSubMenuItem[];
  submenuPaths?: string[];
  /**
   * If the main page has multiple sub routes
   */
  paths?: string[];
  params?: string[];
  SubmenuTitle?: FC;
  SubmenuIcon?: FC;
  showWarning?: boolean;
  exact?: boolean;
  dividerAbove?: boolean;
  sectionLabelAbove?: string;
  visible: boolean;
  dataTestId: string;
} & (
  | {
      /**
       * Use the iconName instead
       * @deprecated
       */
      Icon: FC;
    }
  | { iconName: TIconName }
);

export const getDrawerMenu = ({
  isCompanyDataValid,
  plan,
  isComponentChain,
  isSharedDocument,
  isInternalDocument,
  isSiteCluster,
  isPlotsActive,
  isFarmSite,
}: {
  isCompanyDataValid: boolean;
  plan: TCustomerPlanType;
  // TODO: Workaround to only show the correct submenu item in the drawer. Refactor that
  isComponentChain: boolean;
  isInternalDocument: boolean;
  isSharedDocument: boolean;
  isSiteCluster: boolean;
  isPlotsActive: boolean;
  isFarmSite: boolean;
}): TDrawerMenuItem[] => {
  const isFreemium = plan === ECustomerPlan.FREEMIUM;

  return [
    {
      label: 'Home',
      iconName: 'home',
      exact: true,
      to: '/',
      visible: true,
      dataTestId: 'menu-home',
    },
    {
      label: 'Origin data import',
      iconName: 'import',
      to: '/imports',
      exact: false,
      visible: !isFreemium,
      dataTestId: 'menu-origin-data-import',
    },
    // Management section
    {
      label: 'Partners',
      iconName: 'partners',
      to: '/partners',
      sectionLabelAbove: 'Management',
      submenuPaths: [
        '/partners/:id',
        '/partners/:id/sites',
        '/partners/:id/components',
        '/partners/:id/components/:componentId',
        '/partners/:id/component-chains',
        '/partners/:id/activities',
        '/partners/:id/documents',
        '/partners/:id/documents/:documentId',
        '/partners/:id/requests',
        '/partners/:id/requests/:requestId',
        '/partners/:id/imports',
        '/partners/:id/imports/farms',
        '/partners/:id/imports/farms/:importId',
        '/partners/:id/imports/datasets',
        '/partners/:id/imports/datasets/:geoDataId',
      ],
      params: ['id', 'componentId', 'importId', 'geoDataId', 'requestId', 'documentId'],
      exact: true,
      visible: true,
      SubmenuTitle: PartnerName,
      SubmenuIcon: PartnerPlaceholderIcon,
      submenu: [
        {
          label: 'Partner overview',
          to: ({ id }) => `/partners/${id}`,
          iconName: 'partner',
          activeClassStyle: {}, // Empty object to override default svg color
          visible: true,
          dataTestId: 'submenu-partner-overview',
        },
        {
          label: 'Partner sites',
          to: ({ id }) => `/partners/${id}/sites`,
          iconName: 'partner',
          activeRoutes: ['/partners/:id/sites'],
          dataTestId: 'submenu-partner-sites',
          visible: true,
        },
        {
          label: 'Partner component chains',
          to: ({ id }) => `/partners/${id}/component-chains`,
          iconName: 'partner',
          activeRoutes: ['/partners/:id/components', '/partners/:id/component-chains'],
          dataTestId: 'submenu-partner-component-chains',
          visible: true,
        },
        {
          label: 'Partner activities',
          to: ({ id }) => `/partners/${id}/activities`,
          iconName: 'activities',
          visible: true,
          dataTestId: 'submenu-partner-activities',
        },
        {
          label: 'Partner documents',
          to: ({ id }) => `/partners/${id}/documents`,
          iconName: 'documents',
          activeRoutes: ['/partners/:id/documents/:documentId'],
          activeClassStyle: { '& path': { fill: theme.palette.secondary.main } },
          visible: !isFreemium,
          dataTestId: 'submenu-partner-documents',
        },
        {
          label: 'Partner requests',
          to: ({ id }) => `/partners/${id}/requests`,
          iconName: 'document-request-pending',
          visible: !isFreemium,
          activeRoutes: ['/partners/:id/requests/:requestId'],
          activeClassStyle: { '& path': { fill: theme.palette.secondary.main } },
          dataTestId: 'submenu-partner-requests',
        },
        {
          label: 'Origin data import',
          iconName: 'import',
          to: ({ id }) => `/partners/${id}/imports`,
          activeRoutes: [
            '/partners/:id/imports',
            '/partners/:id/imports/farms',
            '/partners/:id/imports/farms/:importId',
            '/partners/:id/imports/datasets',
            '/partners/:id/imports/datasets/:datasetId',
          ],
          visible: !isFreemium, // Only accessible for clients
          dataTestId: 'submenu-origin-data-import',
        },
      ],
      dataTestId: 'menu-partners',
    },
    {
      label: 'Supply chains',
      iconName: 'supply-chain',
      to: '/supply-chains/active',
      params: ['productId', 'chainId'],
      submenuPaths: [
        '/supply-chains/active/:productId/:chainId',
        '/supply-chains/active/:productId/:chainId/editor',
      ],
      SubmenuTitle: ProductTitle,
      SubmenuIcon: ProductPlaceholderIcon,
      submenu: [
        {
          label: 'Supply chain overview',
          to: ({ productId, chainId }) => `/supply-chains/active/${productId}/${chainId}`,
          iconName: 'supply-chain',
          activeRoutes: ['/supply-chains/active/:productId/:chainId'],
          visible: true,
          dataTestId: 'submenu-supply-chain-overview',
        },
        {
          label: 'Supply chain mapping',
          to: ({ productId, chainId }) => `/supply-chains/active/${productId}/${chainId}/editor`,
          iconName: 'supply-chain-edit',
          activeRoutes: ['/supply-chains/active/:productId/:chainId/editor'],
          visible: true,
          dataTestId: 'submenu-supply-chain-mapping',
        },
      ],
      visible: !isFreemium,
      dataTestId: 'menu-supply-chains',
    },
    {
      label: 'Component chains',
      iconName: 'component-chain',
      to: '/component-chains',
      params: ['id'],
      paths: ['/components', '/component-chains'],
      submenuPaths: ['/components/:id', '/component-chains/:id', '/component-chains/:id/editor'],
      SubmenuTitle: DrawerComponentItemTitle,
      SubmenuIcon: DrawerComponentItemIcon,
      submenu: [
        {
          label: 'Component overview',
          to: ({ id }) => `/components/${id}`,
          iconName: 'component',
          visible: !isComponentChain,
          dataTestId: 'submenu-component-overview',
        },
        {
          label: 'Component chain overview',
          to: ({ id }) => `/component-chains/${id}`,
          iconName: 'component-chain',
          visible: isComponentChain,
          dataTestId: 'submenu-component-chain-overview',
        },
        {
          label: 'Component chain mapping',
          to: ({ id }) => `/component-chains/${id}/editor`,
          iconName: 'component-chain',
          visible: isComponentChain,
          dataTestId: 'submenu-component-chain-mapping',
        },
      ],
      visible: true,
      dataTestId: 'menu-component-chains',
    },
    {
      label: 'Sites',
      iconName: 'locations',
      to: '/sites',
      submenuPaths: [
        '/sites/:id',
        '/sites/:id/sites',
        '/sites/:id/documents/internal',
        '/sites/:id/documents/shared',
        '/sites/:id/activities',
        '/sites/:id/plots',
        '/sites/:id/plots/:plotId',
        '/sites/:id/plots/:plotId/documents/internal',
        '/sites/:id/plots/:plotId/documents/shared',
      ],
      SubmenuTitle: isPlotsActive ? PlotTitle : SiteTitle,
      params: ['id', 'plotId'],
      exact: true,
      SubmenuIcon: () => <Icon name="site-placeholder" color="white" />,
      submenu: [
        {
          label: 'Site overview',
          to: ({ id }) => `/sites/${id}`,
          iconName: 'site-placeholder',
          visible: !isSiteCluster && !isPlotsActive,
          dataTestId: 'submenu-site-overview',
        },
        {
          label: 'Cluster overview',
          to: ({ id }) => `/sites/${id}`,
          iconName: 'site-placeholder',
          visible: isSiteCluster && !isPlotsActive,
          dataTestId: 'submenu-site-overview-internal',
        },
        {
          label: 'Cluster sites',
          to: ({ id }) => `/sites/${id}/sites`,
          iconName: 'site-cluster-placeholder',
          visible: isSiteCluster && !isPlotsActive,
          dataTestId: 'submenu-cluster-sites',
        },
        {
          label: 'Site activities',
          to: ({ id }) => `/sites/${id}/activities`,
          iconName: 'activities',
          dataTestId: 'submenu-site-activities',
          visible: !isSiteCluster && !isPlotsActive,
        },
        {
          label: 'Cluster activities',
          to: ({ id }) => `/sites/${id}/activities`,
          iconName: 'activities',
          dataTestId: 'submenu-cluster-activities',
          visible: isSiteCluster && !isPlotsActive,
        },
        {
          label: 'Site documents',
          activeRoutes: ['/sites/:id/documents/internal', '/sites/:id/documents/shared'],
          to: ({ id }) => `/sites/${id}/documents`,
          iconName: 'documents',
          dataTestId: 'submenu-site-documents',
          visible: !isSiteCluster && !isPlotsActive,
        },
        {
          label: 'Cluster documents',
          activeRoutes: ['/sites/:id/documents/internal', '/sites/:id/documents/shared'],
          to: ({ id }) => `/sites/${id}/documents`,
          iconName: 'documents',
          dataTestId: 'submenu-cluster-documents',
          visible: isSiteCluster && !isPlotsActive,
        },
        {
          label: 'Site plots',
          to: ({ id }) => `/sites/${id}/plots`,
          iconName: 'location',
          activeRoutes: ['/sites/:id/plots'],
          dataTestId: 'submenu-site-plots',
          visible: !isSiteCluster && !isPlotsActive && isFarmSite,
        },
        {
          label: 'Plot overview',
          to: ({ id, plotId }) => `/sites/${id}/plots/${plotId}`,
          iconName: 'location',
          activeRoutes: ['/sites/:id/plots/:plotId'],
          visible: isPlotsActive,
          dataTestId: 'submenu-plot-overview',
        },
        {
          label: 'Plot documents',
          to: ({ id, plotId }) => `/sites/${id}/plots/${plotId}/documents/internal`,
          iconName: 'documents',
          activeRoutes: [
            '/sites/:id/plots/:plotId/documents/internal',
            '/sites/:id/plots/:plotId/documents/shared',
          ],
          visible: isPlotsActive,
          dataTestId: 'submenu-plot-documents',
        },
      ],
      visible: true,
      dataTestId: 'menu-sites',
    },
    {
      label: 'Activities',
      iconName: 'activities',
      to: '/activities',
      submenuPaths: ['/activities/:id'],
      SubmenuTitle: ActivityTitle,
      params: ['id'],
      exact: true,
      SubmenuIcon: () => <Icon name="activity-placeholder" color="white" />,
      submenu: [
        {
          label: 'Activity overview',
          to: ({ id }) => `/activities/${id}`,
          iconName: 'activity-placeholder',
          visible: true,
          dataTestId: 'submenu-activity-overview',
        },
      ],
      visible: true,
      dataTestId: 'menu-activities',
    },

    {
      label: 'Documents',
      iconName: 'documents',
      exact: false,
      to: '/documents/internal',
      paths: [
        '/documents/internal',
        '/documents/internal/:documentId',
        '/documents/shared',
        '/documents/shared/:documentId',
        '/documents/requested',
        '/documents/requested/:documentId',
      ],
      submenuPaths: [
        '/documents/internal/:documentId',
        '/documents/shared/:documentId',
        '/documents/requested/:documentId',
      ],
      SubmenuTitle: DocumentTitle,
      SubmenuIcon: DocumentPlaceholderIcon,
      visible: true,
      params: ['documentId'],
      submenu: [
        {
          label: 'Document overview',
          to: ({ documentId }) => `/documents/internal/${documentId}`,
          iconName: 'document',
          visible: isInternalDocument,
          dataTestId: 'submenu-document-overview-internal',
        },
        {
          label: 'Document overview',
          to: ({ documentId }) => `/documents/shared/${documentId}`,
          iconName: 'document',
          visible: isSharedDocument,
          dataTestId: 'submenu-document-overview-shared',
        },
        {
          label: 'Document overview',
          to: ({ documentId }) => `/documents/requested/${documentId}`,
          iconName: 'document',
          visible: !isInternalDocument && !isSharedDocument,
          dataTestId: 'submenu-document-overview-requested',
        },
      ],
      dataTestId: 'menu-documents',
    },
    {
      label: 'Questionnaires',
      iconName: 'documents',
      to: '/questionnaires',
      params: ['questionnaireId', 'questionnaireResponseId'],
      SubmenuTitle: QuestionnaireTitle,
      SubmenuIcon: DocumentPlaceholderIcon,
      submenuPaths: [
        '/questionnaires/:questionnaireId',
        '/questionnaires/:questionnaireId/answers',
        '/questionnaires/:questionnaireId/answers/:questionnaireResponseId',
        '/questionnaires/:questionnaireId/responses',
        '/questionnaires/:questionnaireId/responses/:questionnaireResponseId',
      ],
      visible: true,
      submenu: [
        {
          label: 'Questionnaire overview',
          to: ({ questionnaireId, questionnaireResponseId }) =>
            `/questionnaires/${questionnaireId}/answers/${questionnaireResponseId}`,
          iconName: 'document',
          activeRoutes: ['/questionnaires/:questionnaireId/answers/:questionnaireResponseId'],
          visible: isFreemium,
          dataTestId: 'submenu-questionnaire-overview',
        },
        {
          label: 'Questionnaire overview',
          to: ({ questionnaireId }) => `/questionnaires/${questionnaireId}`,
          iconName: 'document',
          activeRoutes: ['/questionnaires/:questionnaireId'],
          visible: !isFreemium,
          dataTestId: 'submenu-questionnaire-overview',
        },
        {
          label: 'Questionnaire responses',
          to: ({ questionnaireId, questionnaireResponseId }) =>
            `/questionnaires/${questionnaireId}/responses${
              questionnaireResponseId ? `/${questionnaireResponseId}` : ''
            }`,
          iconName: 'document',
          activeRoutes: [
            '/questionnaires/:questionnaireId/responses',
            '/questionnaires/:questionnaireId/responses/:questionnaireResponseId',
          ],
          visible: !isFreemium,
          dataTestId: 'submenu-questionnaire-responses',
        },
      ],
      dataTestId: 'menu-questionnaires',
    },
    {
      label: 'Requests',
      iconName: 'document-request-pending',
      to: '/requests',
      visible: isFreemium,
      dataTestId: 'menu-requests',
    },
    {
      label: 'Origin data import',
      iconName: 'import',
      to: '/imports',
      exact: false,
      visible: isFreemium,
      dataTestId: 'menu-origin-data-import',
    },
    // Communication section
    {
      label: 'Product pages',
      iconName: 'products',
      to: '/products/active',
      paths: ['/products/active', '/products/archived'],
      submenuPaths: [
        '/products/active/:id',
        '/products/active/:id/editor',
        '/products/active/:id/analytics',
        '/products/active/:id/analytics/behavior',
        '/products/active/:id/analytics/embedded',
        '/products/active/:id/feedback',
      ],
      sectionLabelAbove: !isFreemium ? 'Communication' : undefined,
      SubmenuTitle: ProductTitle,
      SubmenuIcon: ProductPlaceholderIcon,
      params: ['id'],
      exact: true,
      visible: !isFreemium,
      submenu: [
        {
          label: 'Product page overview',
          to: ({ id }) => `/products/active/${id}`,
          iconName: 'product',
          visible: true,
          activeRoutes: [
            '/products/active/:id',
            '/products/active/:id/analytics',
            '/products/active/:id/analytics/behavior',
            '/products/active/:id/analytics/embedded',
            '/products/active/:id/feedback',
          ],
          dataTestId: 'submenu-product-page-overview',
        },
        {
          label: 'Product page editor',
          to: ({ id }) => `/products/active/${id}/editor`,
          iconName: 'product-edit',
          visible: true,
          dataTestId: 'submenu-product-page-editor',
        },
      ],
      dataTestId: 'menu-product-pages',
    },
    {
      label: 'Impact Claims',
      iconName: 'claims',
      to: '/impact-claims/active',
      paths: ['impact-claims/in-progress', 'impact-claims/invalid', '/impact-claims/active'],
      submenuPaths: ['/impact-claims/active/:id'],
      params: ['id'],
      exact: true,
      SubmenuTitle: ClaimTitle,
      SubmenuIcon: ImpactClaimPlaceholderIcon,
      visible: !isFreemium,
      submenu: [
        {
          label: 'Claim overview',
          to: ({ id }) => `/impact-claims/active/${id}`,
          Icon: ClaimIcon,
          visible: true,
          dataTestId: 'submenu-claim-overview',
        },
      ],
      dataTestId: 'menu-impact-claims',
    },
    {
      label: 'Media',
      iconName: 'media',
      exact: true,
      to: '/media-library',
      visible: true,
      dataTestId: 'menu-media',
    },
    // Settings and help section
    {
      label: 'Settings',
      iconName: 'settings-filled',
      to: '/settings',
      showWarning: !isCompanyDataValid,
      dividerAbove: true,
      visible: true,
      dataTestId: 'menu-settings',
    },
  ];
};
